/**
 * @file Helpers for managing client-side state related to child data.
 */

import { ChildData } from "../../types";

/**
 * Stores the provided child data in session storage under a given storage key.
 *
 * TODO: include an expiry timestamp along with the childData object stored.
 *
 * @param {ChildData} childData - The data object to be stored in session storage.
 * @param {string} [storageKey=window.location.pathname] - The key used to store the child data, defaulting to the pathname if not provided.
 * @returns {any} The stored child data object.
 */
export const setStoredChildData = (
  childData: ChildData,
  storageKey: string = window.location.pathname
): object => {
  sessionStorage.setItem(`childData:${storageKey}`, JSON.stringify(childData));
  return childData;
};

/**
 * Retrieves the stored child data from session storage for a given storage key.
 *
 * @param {string} [storageKey=window.location.pathname] - The key used to retrieve the child data, defaulting to the pathname if not provided.
 * @returns {ChildData | null} The parsed child data object if found, otherwise `null`.
 */
export const getStoredChildData = (
  storageKey: string = window.location.pathname
): ChildData | null => {
  const storedChildData = sessionStorage.getItem(`childData:${storageKey}`);
  return storedChildData ? JSON.parse(storedChildData) : null;
};

/**
 * Removes the stored child data from session storage for a given storage key.
 *
 * @param {string} [storageKey=window.location.pathname] - The key used to remove the child data, defaulting to the pathname if not provided.
 */
export const removeStoredChildData = (storageKey: string = window.location.pathname): void => {
  sessionStorage.removeItem(`childData:${storageKey}`);
};
